import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box } from "@mui/material";
import { SearchHelpInput, TextInput, SelectInput } from "../../../common/form-controls";
import { MenuItem } from "../../../common/menu";
import SearchHelpDialog from "../../../common/search-helps";
import { getSystemGroups } from "../../../../utils/searchHelps";
import { CreateRoleForRequestFormState } from "@/types/brm";
import { usersService } from "../../../../services/users-service";
import { DropdownSearchHelpTagInput } from "../../../common/form-controls/dropdown-search-help-input";
import "./styles.less";

type InputProps = {
    value: string;
    setValue: (value: string) => void;
    error?: { value: string | { key: string; text: string }[]; message: string };
    disabled?: boolean;
};

type BooleanInputProps = {
    value: boolean;
    setValue: (value: boolean) => void;
};

const RoleNameInput: React.FC<InputProps> = ({ value, setValue, error }) => {
    const intl = useIntl();
    return (
        <TextInput
            label={intl.formatMessage({ id: "create-role-for-request-form.input-role-name" })}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            error={error?.value === value}
            errorMessage={error?.message}
        />
    );
};

const SystemGroupInput: React.FC<InputProps> = ({ value, setValue, error, disabled }) => {
    const intl = useIntl();
    const title = useMemo(() => intl.formatMessage({ id: "create-role-for-request-form.input-landscape" }), [intl]);
    const columns = useMemo(
        () => [
            { id: "id", title: intl.formatMessage({ id: "search-help.columns.system" }), width: "200px" },
            { id: "description", title: intl.formatMessage({ id: "search-help.columns.description" }), width: "400px" },
        ],
        [intl]
    );
    const [open, setOpen] = useState(false);

    const fetchSystemGroups = useCallback((params, signal) => (open ? getSystemGroups(params, signal) : null), [open]);

    return (
        <>
            <SearchHelpInput
                label={title}
                value={value}
                error={error?.value === value}
                errorMessage={error?.message}
                onChange={(event) => setValue(event.target.value)}
                onOpenSearchHelp={() => setOpen(true)}
                disabled={disabled}
            />

            <SearchHelpDialog
                defaultSearchString={value}
                title={title}
                columns={columns}
                open={open}
                readRows={fetchSystemGroups}
                onSubmit={(row) => {
                    setValue(row.id);
                    setOpen(false);
                }}
                onCancel={() => setOpen(false)}
                selectOneRow={true}
                currentIds={null}
            />
        </>
    );
};

const RoleDescriptionInput: React.FC<InputProps> = ({ value, setValue, error }) => {
    const intl = useIntl();
    return (
        <TextInput
            multiline
            rows={3}
            label={intl.formatMessage({ id: "create-role-for-request-form.input-role-description" })}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            error={error?.value === value}
            errorMessage={error?.message}
        />
    );
};

const RoleTypeInput: React.FC<BooleanInputProps> = ({ value, setValue }) => {
    const intl = useIntl();
    const types = [
        { isComplex: false, text: <FormattedMessage id="create-role-for-request-form.input-role-type.single" /> },
        { isComplex: true, text: <FormattedMessage id="create-role-for-request-form.input-role-type.group" /> },
    ];

    return (
        <SelectInput
            label={intl.formatMessage({ id: "create-role-for-request-form.input-role-type.label" })}
            value={value ? "true" : "false"}
            onChange={(event) => setValue(event.target.value === "true")}
        >
            {types.map((item) => (
                <MenuItem key={String(item.isComplex)} value={String(item.isComplex)} sx={{ minHeight: "initial", height: "36px" }}>
                    {item.text}
                </MenuItem>
            ))}
        </SelectInput>
    );
};

// const ProfileNameInput: React.FC<InputProps> = ({ value, setValue, error }) => {
//     const intl = useIntl();
//     return (
//         <TextInput
//             label={intl.formatMessage({ id: "create-role-for-request-form.input-profile-name" })}
//             value={value}
//             onChange={(event) => setValue(event.target.value)}
//             error={error?.value === value}
//             errorMessage={error?.message}
//         />
//     );
// };

// const ProfileDescriptionInput: React.FC<InputProps> = ({ value, setValue, error }) => {
//     const intl = useIntl();
//     return (
//         <TextInput
//             multiline
//             rows={3}
//             label={intl.formatMessage({ id: "create-role-for-request-form.input-profile-description" })}
//             value={value}
//             onChange={(event) => setValue(event.target.value)}
//             error={error?.value === value}
//             errorMessage={error?.message}
//         />
//     );
// };

type RoleOwnerInputProps = {
    ids: { key: string; text: string }[];
    setIds: (value: { key: string; text: string }[], item: { key: string; text: string }) => void;
    getUsers: (params, signal) => Promise<object>;
    error?: { value: { key: string; text: string }[] | string; message: string };
    disabledMultiple: boolean;
    setValidationErrors: (errors: Record<string, { value: string; message: string }>) => void;
};

const getUsers = (params) => {
    return usersService.getUsersForSearchHelp(params);
};
const RoleOwnerInput: React.FC<RoleOwnerInputProps> = ({ ids, setIds, error, getUsers, disabledMultiple = false }) => {
    const intl = useIntl();
    const label = intl.formatMessage({ id: "create-role-for-request-form.input-role-owner" });
    return (
        <DropdownSearchHelpTagInput
            //TODO:Починить ерроры
            tags={ids}
            setTags={setIds}
            getData={getUsers}
            error={error?.value === ids}
            errorMessage={error?.message}
            label={label}
            disableMultiple={disabledMultiple}
        />
    );
};

const DeputyRoleOwnerInput: React.FC<RoleOwnerInputProps> = ({ ids, setIds, error, getUsers, disabledMultiple = false }) => {
    const intl = useIntl();
    const label = intl.formatMessage({ id: "create-role-for-request-form.input-deputy-role-owner" });
    return (
        <DropdownSearchHelpTagInput
            tags={ids}
            setTags={setIds}
            getData={getUsers}
            error={error?.value === ids}
            errorMessage={error?.message}
            label={label}
            disableMultiple={disabledMultiple}
        />
    );
};

type FormProps = {
    form: CreateRoleForRequestFormState;
    setForm: (form: Partial<FormProps["form"]>) => void;
    validationErrors: Record<string, { message: string; value: string | { key: string; text: string }[] }> | null;
    setValidationErrors: (errors: Record<string, { value: string; message: string }>) => void;
    groupIdDisabled: boolean;
};

const CreateRoleForRequestForm: React.FC<FormProps> = ({ form, setForm, validationErrors, setValidationErrors, groupIdDisabled }) => {
    return (
        <>
            <Box className="create-role-for-request-form">
                <RoleNameInput value={form.id} setValue={(value) => setForm({ id: value })} error={validationErrors?.id} />
                <RoleDescriptionInput value={form.description} setValue={(value) => setForm({ description: value })} error={validationErrors?.description} />
                <SystemGroupInput value={form.groupId} setValue={(value) => setForm({ groupId: value })} error={validationErrors?.groupId} disabled={groupIdDisabled} />
                <RoleTypeInput value={form.isComplex} setValue={(value) => setForm({ isComplex: value })} />
                {/* <ProfileNameInput value={form.profileName} setValue={(value) => setForm({ profileName: value })} error={validationErrors?.profileName} />
                <ProfileDescriptionInput
                    value={form.profileDescription}
                    setValue={(value) => setForm({ profileDescription: value })}
                    error={validationErrors?.profileDescription}
                /> */}
                <RoleOwnerInput
                    ids={form.roleOwner}
                    setIds={(value: { key: string; text: string }[], item: { key: string; text: string }) => setForm({ roleOwner: value, roleOwnerFullname: item?.text ?? "" })}
                    error={validationErrors?.roleOwner}
                    disabledMultiple={true}
                    getUsers={getUsers}
                    setValidationErrors={setValidationErrors}
                />
                <DeputyRoleOwnerInput
                    ids={form.deputyRoleOwner}
                    setIds={(value, item) => setForm({ deputyRoleOwner: value, deputyRoleOwnerFullname: item?.text ?? "" })}
                    error={validationErrors?.deputyRoleOwner}
                    disabledMultiple={true}
                    getUsers={getUsers}
                    setValidationErrors={setValidationErrors}
                />
            </Box>
        </>
    );
};

export default CreateRoleForRequestForm;
